/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpResponse, HttpContext, HttpParams } from '@angular/common/http';
import { DataBaseService } from '../data-base-service';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ArchiveDataEntryDtoModel } from '../models/archive-data-entry-dto-model';
import { BatchCreateEntriesRequestDto } from '../models/batch-create-entries-request-dto';
import { BatchCreateEntriesResponseDto } from '../models/batch-create-entries-response-dto';
import { BulkUpdateEntries } from '../models/bulk-update-entries';
import { BulkUpdateResponseDto } from '../models/bulk-update-response-dto';
import { BulkUpgradeEntries } from '../models/bulk-upgrade-entries';
import { BulkUpgradeResponseDto } from '../models/bulk-upgrade-response-dto';
import { CreateDataEntryDto } from '../models/create-data-entry-dto';
import { CurrencyDto } from '../models/currency-dto';
import { DataEntriesResponseV3 } from '../models/data-entries-response-v-3';
import { DataEntryEmailDto } from '../models/data-entry-email-dto';
import { DataEntryExcludingSearchIndex } from '../models/data-entry-excluding-search-index';
import { DataEntryHistory } from '../models/data-entry-history';
import { DataEntryKanbanViewDto } from '../models/data-entry-kanban-view-dto';
import { DataEntryPartialExcludingCreationInfoUpdateInfoStateHistoryIdIsInvalidInvalidReasonSearchIndex } from '../models/data-entry-partial-excluding-creation-info-update-info-state-history-id-is-invalid-invalid-reason-search-index';
import { DataEntryTreeDto } from '../models/data-entry-tree-dto';
import { DataEntryTreeDtoV2 } from '../models/data-entry-tree-dto-v-2';
import { DataEntryV2Body } from '../models/data-entry-v-2-body';
import { GetEntriesForFieldRequestDto } from '../models/get-entries-for-field-request-dto';
import { IsEditableFieldDto } from '../models/is-editable-field-dto';
import { JsonPatchOperation } from '../models/json-patch-operation';
import { MappingFieldSourcesDto } from '../models/mapping-field-sources-dto';
import { NewDataEntry } from '../models/new-data-entry';
import { PatchDataEntryDtoV3 } from '../models/patch-data-entry-dto-v-3';
import { ReferenceProperty } from '../models/reference-property';
import { SelectAllDto } from '../models/select-all-dto';
import { UpdateDataEntryDto } from '../models/update-data-entry-dto';
import { UpdateEntryPositionDto } from '../models/update-entry-position-dto';
import { Count as LoopbackCount } from '../models/loopback/count';

@Injectable({ providedIn: 'root' })
export class DataEntriesControllerService extends DataBaseService {

  /**
   * Path part for operation dataEntriesControllerFindCurrencies
   */
  static readonly DataEntriesControllerFindCurrenciesPath = '/v1/currencies';

  /**
   * List all currencies
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findCurrencies()` instead.
   *
   * This method doesn't expect any request body.
   */
  findCurrencies$Response(params?: {
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<CurrencyDto>>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindCurrenciesPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<CurrencyDto>>;
      })
    );
  }
  /**
   * List all currencies
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findCurrencies$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findCurrencies(params?: {
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<CurrencyDto>> {

    return this.findCurrencies$Response(params).pipe(
      map((r: StrictHttpResponse<Array<CurrencyDto>>) => r.body as Array<CurrencyDto>)
    );
  }

  /**
   * Path part for operation dataEntriesControllerArchiveEntries
   */
  static readonly DataEntriesControllerArchiveEntriesPath = '/v1/entities/{entity}/archive/entries';

  /**
   * Delete a all data entries that belongs to an entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `archiveEntries()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  archiveEntries$Response(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: Array<any>
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerArchiveEntriesPath, 'delete');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }
  /**
   * Delete a all data entries that belongs to an entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `archiveEntries$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  archiveEntries(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: Array<any>
  }): Observable<void> {

    return this.archiveEntries$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation dataEntriesControllerBulkUpdateEntries
   */
  static readonly DataEntriesControllerBulkUpdateEntriesPath = '/v1/entities/{entity}/bulk-update';

  /**
   * Bulk update data entries
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `bulkUpdateEntries()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkUpdateEntries$Response(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: BulkUpdateEntries
  }): Observable<StrictHttpResponse<BulkUpdateResponseDto>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerBulkUpdateEntriesPath, 'patch');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BulkUpdateResponseDto>;
      })
    );
  }
  /**
   * Bulk update data entries
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `bulkUpdateEntries$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkUpdateEntries(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: BulkUpdateEntries
  }): Observable<BulkUpdateResponseDto> {

    return this.bulkUpdateEntries$Response(params).pipe(
      map((r: StrictHttpResponse<BulkUpdateResponseDto>) => r.body as BulkUpdateResponseDto)
    );
  }

  /**
   * Path part for operation dataEntriesControllerBulkUpgradeEntries
   */
  static readonly DataEntriesControllerBulkUpgradeEntriesPath = '/v1/entities/{entity}/bulk-upgrade';

  /**
   * Bulk upgrade data entries
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `bulkUpgradeEntries()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   *
   * @deprecated
   */
  bulkUpgradeEntries$Response(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: BulkUpgradeEntries
  }): Observable<StrictHttpResponse<BulkUpgradeResponseDto>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerBulkUpgradeEntriesPath, 'patch');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BulkUpgradeResponseDto>;
      })
    );
  }
  /**
   * Bulk upgrade data entries
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `bulkUpgradeEntries$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   *
   * @deprecated
   */
  bulkUpgradeEntries(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: BulkUpgradeEntries
  }): Observable<BulkUpgradeResponseDto> {

    return this.bulkUpgradeEntries$Response(params).pipe(
      map((r: StrictHttpResponse<BulkUpgradeResponseDto>) => r.body as BulkUpgradeResponseDto)
    );
  }

  /**
   * Path part for operation dataEntriesControllerBatchCreate
   */
  static readonly DataEntriesControllerBatchCreatePath = '/v1/entities/{entityId}/entries/batch';

  /**
   * Create a main entry and related entries in a single operation
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `batchCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  batchCreate$Response(params: {
    entityId: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: BatchCreateEntriesRequestDto
  }): Observable<StrictHttpResponse<BatchCreateEntriesResponseDto>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerBatchCreatePath, 'post');
    if (params) {
      rb.path('entityId', params.entityId, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BatchCreateEntriesResponseDto>;
      })
    );
  }
  /**
   * Create a main entry and related entries in a single operation
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `batchCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  batchCreate(params: {
    entityId: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: BatchCreateEntriesRequestDto
  }): Observable<BatchCreateEntriesResponseDto> {

    return this.batchCreate$Response(params).pipe(
      map((r: StrictHttpResponse<BatchCreateEntriesResponseDto>) => r.body as BatchCreateEntriesResponseDto)
    );
  }

  /**
   * Path part for operation dataEntriesControllerCount
   */
  static readonly DataEntriesControllerCountPath = '/v1/entities/{entity}/entries/count';

  /**
   * Counts the number of data entries that belong to an entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `count()` instead.
   *
   * This method doesn't expect any request body.
   */
  count$Response(params: {
    entity: string;
    where?: any;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<LoopbackCount>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerCountPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.query('where', params.where, {});
      rb.query('include_virtual', params.include_virtual, {});
      rb.query('appliedQuickFilters', params.appliedQuickFilters, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LoopbackCount>;
      })
    );
  }
  /**
   * Counts the number of data entries that belong to an entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `count$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  count(params: {
    entity: string;
    where?: any;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<LoopbackCount> {

    return this.count$Response(params).pipe(
      map((r: StrictHttpResponse<LoopbackCount>) => r.body as LoopbackCount)
    );
  }

  /**
   * Path part for operation dataEntriesControllerCountDraftEntries
   */
  static readonly DataEntriesControllerCountDraftEntriesPath = '/v1/entities/{entity}/entries/draft/count';

  /**
   * Counts the number of draft entries that belongs to the current user and entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `countDraftEntries()` instead.
   *
   * This method doesn't expect any request body.
   */
  countDraftEntries$Response(params: {
    entity: string;
    filterCriteria?: any;
    search?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<LoopbackCount>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerCountDraftEntriesPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.query('filterCriteria', params.filterCriteria, {});
      rb.query('search', params.search, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LoopbackCount>;
      })
    );
  }
  /**
   * Counts the number of draft entries that belongs to the current user and entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `countDraftEntries$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  countDraftEntries(params: {
    entity: string;
    filterCriteria?: any;
    search?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<LoopbackCount> {

    return this.countDraftEntries$Response(params).pipe(
      map((r: StrictHttpResponse<LoopbackCount>) => r.body as LoopbackCount)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindDraftEntryById
   */
  static readonly DataEntriesControllerFindDraftEntryByIdPath = '/v1/entities/{entity}/entries/draft/{id}';

  /**
   * Get a single draft entry that belongs to the current user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findDraftEntryById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findDraftEntryById$Response(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<DataEntryExcludingSearchIndex>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindDraftEntryByIdPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntryExcludingSearchIndex>;
      })
    );
  }
  /**
   * Get a single draft entry that belongs to the current user
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findDraftEntryById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findDraftEntryById(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<DataEntryExcludingSearchIndex> {

    return this.findDraftEntryById$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntryExcludingSearchIndex>) => r.body as DataEntryExcludingSearchIndex)
    );
  }

  /**
   * Path part for operation dataEntriesControllerDeleteDraftEntry
   */
  static readonly DataEntriesControllerDeleteDraftEntryPath = '/v1/entities/{entity}/entries/draft/{id}';

  /**
   * Delete a single draft entry that belongs to the current user and entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteDraftEntry()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteDraftEntry$Response(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<any>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerDeleteDraftEntryPath, 'delete');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<any>;
      })
    );
  }
  /**
   * Delete a single draft entry that belongs to the current user and entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteDraftEntry$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteDraftEntry(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<any> {

    return this.deleteDraftEntry$Response(params).pipe(
      map((r: StrictHttpResponse<any>) => r.body as any)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindDraftEntries
   */
  static readonly DataEntriesControllerFindDraftEntriesPath = '/v1/entities/{entity}/entries/draft';

  /**
   * Get a list of draft entries that belongs to the current user and entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findDraftEntries()` instead.
   *
   * This method doesn't expect any request body.
   */
  findDraftEntries$Response(params: {
    entity: string;
    filterCriteria?: any;
    search?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<DataEntriesResponseV3>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindDraftEntriesPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.query('filterCriteria', params.filterCriteria, {});
      rb.query('search', params.search, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntriesResponseV3>;
      })
    );
  }
  /**
   * Get a list of draft entries that belongs to the current user and entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findDraftEntries$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findDraftEntries(params: {
    entity: string;
    filterCriteria?: any;
    search?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<DataEntriesResponseV3> {

    return this.findDraftEntries$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntriesResponseV3>) => r.body as DataEntriesResponseV3)
    );
  }

  /**
   * Path part for operation dataEntriesControllerUpsertDraftEntry
   */
  static readonly DataEntriesControllerUpsertDraftEntryPath = '/v1/entities/{entity}/entries/draft';

  /**
   * Create or update a draft entry
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `upsertDraftEntry()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  upsertDraftEntry$Response(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: CreateDataEntryDto
  }): Observable<StrictHttpResponse<DataEntryExcludingSearchIndex>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerUpsertDraftEntryPath, 'post');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntryExcludingSearchIndex>;
      })
    );
  }
  /**
   * Create or update a draft entry
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `upsertDraftEntry$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  upsertDraftEntry(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: CreateDataEntryDto
  }): Observable<DataEntryExcludingSearchIndex> {

    return this.upsertDraftEntry$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntryExcludingSearchIndex>) => r.body as DataEntryExcludingSearchIndex)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindKanbanDraft
   */
  static readonly DataEntriesControllerFindKanbanDraftPath = '/v1/entities/{entity}/entries/kanban/{categoryFieldId}/draft';

  /**
   * Get a kanban view of draft data entries that belong to the user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findKanbanDraft()` instead.
   *
   * This method doesn't expect any request body.
   */
  findKanbanDraft$Response(params: {
    entity: string;
    categoryFieldId: string;
    filterCriteria?: any;
    collectionId?: string;
    categoryId?: string;
    search?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<DataEntryKanbanViewDto>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindKanbanDraftPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('categoryFieldId', params.categoryFieldId, {});
      rb.query('filterCriteria', params.filterCriteria, {});
      rb.query('collectionId', params.collectionId, {});
      rb.query('categoryId', params.categoryId, {});
      rb.query('search', params.search, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntryKanbanViewDto>;
      })
    );
  }
  /**
   * Get a kanban view of draft data entries that belong to the user
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findKanbanDraft$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findKanbanDraft(params: {
    entity: string;
    categoryFieldId: string;
    filterCriteria?: any;
    collectionId?: string;
    categoryId?: string;
    search?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<DataEntryKanbanViewDto> {

    return this.findKanbanDraft$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntryKanbanViewDto>) => r.body as DataEntryKanbanViewDto)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindKanban
   */
  static readonly DataEntriesControllerFindKanbanPath = '/v1/entities/{entity}/entries/kanban/{categoryFieldId}';

  /**
   * Get a kanban view of data entries that belong to an entity and applied query tags
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findKanban()` instead.
   *
   * This method doesn't expect any request body.
   */
  findKanban$Response(params: {
    entity: string;
    categoryFieldId: string;
    filterCriteria?: any;
    search?: string;
    collectionId?: string;
    categoryId?: string;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    appliedTagsFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<DataEntryKanbanViewDto>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindKanbanPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('categoryFieldId', params.categoryFieldId, {});
      rb.query('filterCriteria', params.filterCriteria, {});
      rb.query('search', params.search, {});
      rb.query('collectionId', params.collectionId, {});
      rb.query('categoryId', params.categoryId, {});
      rb.query('include_virtual', params.include_virtual, {});
      rb.query('appliedQuickFilters', params.appliedQuickFilters, {});
      rb.query('appliedTagsFilters', params.appliedTagsFilters, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntryKanbanViewDto>;
      })
    );
  }
  /**
   * Get a kanban view of data entries that belong to an entity and applied query tags
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findKanban$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findKanban(params: {
    entity: string;
    categoryFieldId: string;
    filterCriteria?: any;
    search?: string;
    collectionId?: string;
    categoryId?: string;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    appliedTagsFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<DataEntryKanbanViewDto> {

    return this.findKanban$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntryKanbanViewDto>) => r.body as DataEntryKanbanViewDto)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindTree
   */
  static readonly DataEntriesControllerFindTreePath = '/v1/entities/{entity}/entries/tree';

  /**
   * Get a list of data entries that childs to another entry
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findTree()` instead.
   *
   * This method doesn't expect any request body.
   */
  findTree$Response(params: {
    entity: string;
    startingItem?: string;
    field?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<DataEntryTreeDto>>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindTreePath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.query('startingItem', params.startingItem, {});
      rb.query('field', params.field, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<DataEntryTreeDto>>;
      })
    );
  }
  /**
   * Get a list of data entries that childs to another entry
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findTree$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findTree(params: {
    entity: string;
    startingItem?: string;
    field?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<DataEntryTreeDto>> {

    return this.findTree$Response(params).pipe(
      map((r: StrictHttpResponse<Array<DataEntryTreeDto>>) => r.body as Array<DataEntryTreeDto>)
    );
  }

  /**
   * Path part for operation dataEntriesControllerGetEntriesVersionsCount
   */
  static readonly DataEntriesControllerGetEntriesVersionsCountPath = '/v1/entities/{entity}/entries/versions/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getEntriesVersionsCount()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getEntriesVersionsCount$Response(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: null | SelectAllDto
  }): Observable<StrictHttpResponse<Array<{
'version': number;
'count': number;
}>>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerGetEntriesVersionsCountPath, 'post');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<{
        'version': number;
        'count': number;
        }>>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getEntriesVersionsCount$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getEntriesVersionsCount(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: null | SelectAllDto
  }): Observable<Array<{
'version': number;
'count': number;
}>> {

    return this.getEntriesVersionsCount$Response(params).pipe(
      map((r: StrictHttpResponse<Array<{
'version': number;
'count': number;
}>>) => r.body as Array<{
'version': number;
'count': number;
}>)
    );
  }

  /**
   * Path part for operation dataEntriesControllerIsFieldEditable
   */
  static readonly DataEntriesControllerIsFieldEditablePath = '/v1/entities/{entity}/entries/{id}/editable/{fieldId}';

  /**
   * Check if field is editable or not
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `isFieldEditable()` instead.
   *
   * This method doesn't expect any request body.
   */
  isFieldEditable$Response(params: {
    entity: string;
    id: string;
    fieldId: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<IsEditableFieldDto>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerIsFieldEditablePath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.path('fieldId', params.fieldId, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<IsEditableFieldDto>;
      })
    );
  }
  /**
   * Check if field is editable or not
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `isFieldEditable$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  isFieldEditable(params: {
    entity: string;
    id: string;
    fieldId: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<IsEditableFieldDto> {

    return this.isFieldEditable$Response(params).pipe(
      map((r: StrictHttpResponse<IsEditableFieldDto>) => r.body as IsEditableFieldDto)
    );
  }

  /**
   * Path part for operation dataEntriesControllerGetEmails
   */
  static readonly DataEntriesControllerGetEmailsPath = '/v1/entities/{entity}/entries/{id}/emails';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getEmails()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEmails$Response(params: {
    entity: string;
    id: string;
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<DataEntryEmailDto>>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerGetEmailsPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<DataEntryEmailDto>>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getEmails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEmails(params: {
    entity: string;
    id: string;
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<DataEntryEmailDto>> {

    return this.getEmails$Response(params).pipe(
      map((r: StrictHttpResponse<Array<DataEntryEmailDto>>) => r.body as Array<DataEntryEmailDto>)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindEntryHistoryById
   */
  static readonly DataEntriesControllerFindEntryHistoryByIdPath = '/v1/entities/{entity}/entries/{id}/history';

  /**
   * return entry history
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findEntryHistoryById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findEntryHistoryById$Response(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<DataEntryHistory>>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindEntryHistoryByIdPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<DataEntryHistory>>;
      })
    );
  }
  /**
   * return entry history
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findEntryHistoryById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findEntryHistoryById(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<DataEntryHistory>> {

    return this.findEntryHistoryById$Response(params).pipe(
      map((r: StrictHttpResponse<Array<DataEntryHistory>>) => r.body as Array<DataEntryHistory>)
    );
  }

  /**
   * Path part for operation dataEntriesControllerUpdateEntryPosition
   */
  static readonly DataEntriesControllerUpdateEntryPositionPath = '/v1/entities/{entity}/entries/{id}/position';

  /**
   * update data entry _metaOrder
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateEntryPosition()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateEntryPosition$Response(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: UpdateEntryPositionDto
  }): Observable<StrictHttpResponse<DataEntryExcludingSearchIndex>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerUpdateEntryPositionPath, 'put');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntryExcludingSearchIndex>;
      })
    );
  }
  /**
   * update data entry _metaOrder
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateEntryPosition$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateEntryPosition(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: UpdateEntryPositionDto
  }): Observable<DataEntryExcludingSearchIndex> {

    return this.updateEntryPosition$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntryExcludingSearchIndex>) => r.body as DataEntryExcludingSearchIndex)
    );
  }

  /**
   * Path part for operation dataEntriesControllerUpgradeEntry
   */
  static readonly DataEntriesControllerUpgradeEntryPath = '/v1/entities/{entity}/entries/{id}/upgrade';

  /**
   * Upgrad., e data entry and reset the workflow that belongs to the entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `upgradeEntry()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  upgradeEntry$Response(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: DataEntryPartialExcludingCreationInfoUpdateInfoStateHistoryIdIsInvalidInvalidReasonSearchIndex
  }): Observable<StrictHttpResponse<any>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerUpgradeEntryPath, 'patch');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<any>;
      })
    );
  }
  /**
   * Upgrad., e data entry and reset the workflow that belongs to the entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `upgradeEntry$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  upgradeEntry(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: DataEntryPartialExcludingCreationInfoUpdateInfoStateHistoryIdIsInvalidInvalidReasonSearchIndex
  }): Observable<any> {

    return this.upgradeEntry$Response(params).pipe(
      map((r: StrictHttpResponse<any>) => r.body as any)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindById
   */
  static readonly DataEntriesControllerFindByIdPath = '/v1/entities/{entity}/entries/{id}';

  /**
   * Get a single data entry that belongs to an entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById$Response(params: {
    entity: string;
    id: string;
    excludeResolved?: boolean;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<DataEntryExcludingSearchIndex>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindByIdPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.query('excludeResolved', params.excludeResolved, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntryExcludingSearchIndex>;
      })
    );
  }
  /**
   * Get a single data entry that belongs to an entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById(params: {
    entity: string;
    id: string;
    excludeResolved?: boolean;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<DataEntryExcludingSearchIndex> {

    return this.findById$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntryExcludingSearchIndex>) => r.body as DataEntryExcludingSearchIndex)
    );
  }

  /**
   * Path part for operation dataEntriesControllerArchiveEntry
   */
  static readonly DataEntriesControllerArchiveEntryPath = '/v1/entities/{entity}/entries/{id}';

  /**
   * Delete a single data entry that belongs to an entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `archiveEntry()` instead.
   *
   * This method doesn't expect any request body.
   */
  archiveEntry$Response(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<any>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerArchiveEntryPath, 'delete');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<any>;
      })
    );
  }
  /**
   * Delete a single data entry that belongs to an entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `archiveEntry$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  archiveEntry(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<any> {

    return this.archiveEntry$Response(params).pipe(
      map((r: StrictHttpResponse<any>) => r.body as any)
    );
  }

  /**
   * Path part for operation dataEntriesControllerUpdateById
   */
  static readonly DataEntriesControllerUpdateByIdPath = '/v1/entities/{entity}/entries/{id}';

  /**
   * Update a single data entry that belongs to an entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById$Response(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: UpdateDataEntryDto
  }): Observable<StrictHttpResponse<any>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerUpdateByIdPath, 'patch');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<any>;
      })
    );
  }
  /**
   * Update a single data entry that belongs to an entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: UpdateDataEntryDto
  }): Observable<any> {

    return this.updateById$Response(params).pipe(
      map((r: StrictHttpResponse<any>) => r.body as any)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFind
   */
  static readonly DataEntriesControllerFindPath = '/v1/entities/{entity}/entries';

  /**
   * Get a list of data entries that belong to an entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `find()` instead.
   *
   * This method doesn't expect any request body.
   *
   * @deprecated
   */
  find$Response(params: {
    entity: string;
    filter?: any;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<DataEntryExcludingSearchIndex>>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.query('filter', params.filter, {});
      rb.query('include_virtual', params.include_virtual, {});
      rb.query('appliedQuickFilters', params.appliedQuickFilters, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<DataEntryExcludingSearchIndex>>;
      })
    );
  }
  /**
   * Get a list of data entries that belong to an entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `find$Response()` instead.
   *
   * This method doesn't expect any request body.
   *
   * @deprecated
   */
  find(params: {
    entity: string;
    filter?: any;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<DataEntryExcludingSearchIndex>> {

    return this.find$Response(params).pipe(
      map((r: StrictHttpResponse<Array<DataEntryExcludingSearchIndex>>) => r.body as Array<DataEntryExcludingSearchIndex>)
    );
  }

  /**
   * Path part for operation dataEntriesControllerCreate
   */
  static readonly DataEntriesControllerCreatePath = '/v1/entities/{entity}/entries';

  /**
   * Create a new data entry
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `create()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create$Response(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: NewDataEntry
  }): Observable<StrictHttpResponse<DataEntryExcludingSearchIndex>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerCreatePath, 'post');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntryExcludingSearchIndex>;
      })
    );
  }
  /**
   * Create a new data entry
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `create$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: NewDataEntry
  }): Observable<DataEntryExcludingSearchIndex> {

    return this.create$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntryExcludingSearchIndex>) => r.body as DataEntryExcludingSearchIndex)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFetchReferenceFieldData
   */
  static readonly DataEntriesControllerFetchReferenceFieldDataPath = '/v1/entities/{entity}/fields/{field}/data';

  /**
   * Get a list of users as reference fields for reference field
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `fetchReferenceFieldData()` instead.
   *
   * This method doesn't expect any request body.
   */
  fetchReferenceFieldData$Response(params: {
    entity: string;
    field: string;
    relatedEntryId?: string;
    cascadedParentId?: string;
    uiContext?: number;
    includeId?: Array<string>;
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<ReferenceProperty>>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFetchReferenceFieldDataPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('field', params.field, {});
      rb.query('relatedEntryId', params.relatedEntryId, {});
      rb.query('cascadedParentId', params.cascadedParentId, {});
      rb.query('uiContext', params.uiContext, {});
      rb.query('includeId', params.includeId, {});
      rb.query('filter', params.filter, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ReferenceProperty>>;
      })
    );
  }
  /**
   * Get a list of users as reference fields for reference field
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `fetchReferenceFieldData$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  fetchReferenceFieldData(params: {
    entity: string;
    field: string;
    relatedEntryId?: string;
    cascadedParentId?: string;
    uiContext?: number;
    includeId?: Array<string>;
    filter?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<ReferenceProperty>> {

    return this.fetchReferenceFieldData$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ReferenceProperty>>) => r.body as Array<ReferenceProperty>)
    );
  }

  /**
   * Path part for operation dataEntriesControllerGetMappedFieldsData
   */
  static readonly DataEntriesControllerGetMappedFieldsDataPath = '/v1/entities/{entity}/sources/{referenceFieldId}/{referenceDataEntry}';

  /**
   * Get mapped fields related to a reference field
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMappedFieldsData()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMappedFieldsData$Response(params: {
    entity: string;
    referenceFieldId: string;
    referenceDataEntry: string;
    entryId?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<MappingFieldSourcesDto>>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerGetMappedFieldsDataPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('referenceFieldId', params.referenceFieldId, {});
      rb.path('referenceDataEntry', params.referenceDataEntry, {});
      rb.query('entryId', params.entryId, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<MappingFieldSourcesDto>>;
      })
    );
  }
  /**
   * Get mapped fields related to a reference field
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getMappedFieldsData$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMappedFieldsData(params: {
    entity: string;
    referenceFieldId: string;
    referenceDataEntry: string;
    entryId?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<MappingFieldSourcesDto>> {

    return this.getMappedFieldsData$Response(params).pipe(
      map((r: StrictHttpResponse<Array<MappingFieldSourcesDto>>) => r.body as Array<MappingFieldSourcesDto>)
    );
  }

  /**
   * Path part for operation dataEntriesControllerArchiveEntriesV2
   */
  static readonly DataEntriesControllerArchiveEntriesV2Path = '/v2/entities/{entity}/archive/entries';

  /**
   * Delete a all data entries that belongs to an entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `archiveEntriesV2()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  archiveEntriesV2$Response(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: ArchiveDataEntryDtoModel
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerArchiveEntriesV2Path, 'delete');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }
  /**
   * Delete a all data entries that belongs to an entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `archiveEntriesV2$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  archiveEntriesV2(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: ArchiveDataEntryDtoModel
  }): Observable<void> {

    return this.archiveEntriesV2$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation dataEntriesControllerBulkUpgradeEntriesV2
   */
  static readonly DataEntriesControllerBulkUpgradeEntriesV2Path = '/v2/entities/{entity}/bulk-upgrade';

  /**
   * Bulk upgrade data entries v2
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `bulkUpgradeEntriesV2()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkUpgradeEntriesV2$Response(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: BulkUpgradeEntries
  }): Observable<StrictHttpResponse<BulkUpgradeResponseDto>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerBulkUpgradeEntriesV2Path, 'patch');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BulkUpgradeResponseDto>;
      })
    );
  }
  /**
   * Bulk upgrade data entries v2
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `bulkUpgradeEntriesV2$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkUpgradeEntriesV2(params: {
    entity: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: BulkUpgradeEntries
  }): Observable<BulkUpgradeResponseDto> {

    return this.bulkUpgradeEntriesV2$Response(params).pipe(
      map((r: StrictHttpResponse<BulkUpgradeResponseDto>) => r.body as BulkUpgradeResponseDto)
    );
  }

  /**
   * Path part for operation dataEntriesControllerCountV2
   */
  static readonly DataEntriesControllerCountV2Path = '/v2/entities/{entity}/entries/count';

  /**
   * Counts the number of data entries that belong to an entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `countV2()` instead.
   *
   * This method doesn't expect any request body.
   */
  countV2$Response(params: {
    entity: string;
    whereCriteria?: any;
    search?: string;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<LoopbackCount>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerCountV2Path, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.query('whereCriteria', params.whereCriteria, {});
      rb.query('search', params.search, {});
      rb.query('include_virtual', params.include_virtual, {});
      rb.query('appliedQuickFilters', params.appliedQuickFilters, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LoopbackCount>;
      })
    );
  }
  /**
   * Counts the number of data entries that belong to an entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `countV2$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  countV2(params: {
    entity: string;
    whereCriteria?: any;
    search?: string;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<LoopbackCount> {

    return this.countV2$Response(params).pipe(
      map((r: StrictHttpResponse<LoopbackCount>) => r.body as LoopbackCount)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindEntityAndDraftEntryById
   */
  static readonly DataEntriesControllerFindEntityAndDraftEntryByIdPath = '/v2/entities/{entity}/entries/draft/{id}';

  /**
   * Get the entity and draft entry information.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findEntityAndDraftEntryById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findEntityAndDraftEntryById$Response(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<DataEntryV2Body>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindEntityAndDraftEntryByIdPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntryV2Body>;
      })
    );
  }
  /**
   * Get the entity and draft entry information.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findEntityAndDraftEntryById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findEntityAndDraftEntryById(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<DataEntryV2Body> {

    return this.findEntityAndDraftEntryById$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntryV2Body>) => r.body as DataEntryV2Body)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindTreeV2
   */
  static readonly DataEntriesControllerFindTreeV2Path = '/v2/entities/{entity}/entries/tree';

  /**
   * Get a list of data entries that child to another entry with applied tags filters
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findTreeV2()` instead.
   *
   * This method doesn't expect any request body.
   */
  findTreeV2$Response(params: {
    entity: string;
    startingItem?: string;
    field?: string;
    appliedTagsFilters?: Array<string>;
    appliedQuickFilters?: Array<string>;
    expandAllChildren?: boolean;
    filterCriteria?: any;
    search?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<DataEntryTreeDtoV2>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindTreeV2Path, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.query('startingItem', params.startingItem, {});
      rb.query('field', params.field, {});
      rb.query('appliedTagsFilters', params.appliedTagsFilters, {});
      rb.query('appliedQuickFilters', params.appliedQuickFilters, {});
      rb.query('expandAllChildren', params.expandAllChildren, {});
      rb.query('filterCriteria', params.filterCriteria, {});
      rb.query('search', params.search, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntryTreeDtoV2>;
      })
    );
  }
  /**
   * Get a list of data entries that child to another entry with applied tags filters
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findTreeV2$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findTreeV2(params: {
    entity: string;
    startingItem?: string;
    field?: string;
    appliedTagsFilters?: Array<string>;
    appliedQuickFilters?: Array<string>;
    expandAllChildren?: boolean;
    filterCriteria?: any;
    search?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<DataEntryTreeDtoV2> {

    return this.findTreeV2$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntryTreeDtoV2>) => r.body as DataEntryTreeDtoV2)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindEntryHistoryByIdV2
   */
  static readonly DataEntriesControllerFindEntryHistoryByIdV2Path = '/v2/entities/{entity}/entries/{id}/history';

  /**
   * return entry history
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findEntryHistoryByIdV2()` instead.
   *
   * This method doesn't expect any request body.
   */
  findEntryHistoryByIdV2$Response(params: {
    entity: string;
    id: string;
    lastTimeStamp?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<DataEntryHistory>>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindEntryHistoryByIdV2Path, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.query('lastTimeStamp', params.lastTimeStamp, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<DataEntryHistory>>;
      })
    );
  }
  /**
   * return entry history
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findEntryHistoryByIdV2$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findEntryHistoryByIdV2(params: {
    entity: string;
    id: string;
    lastTimeStamp?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<DataEntryHistory>> {

    return this.findEntryHistoryByIdV2$Response(params).pipe(
      map((r: StrictHttpResponse<Array<DataEntryHistory>>) => r.body as Array<DataEntryHistory>)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindEntityAndEntryById
   */
  static readonly DataEntriesControllerFindEntityAndEntryByIdPath = '/v2/entities/{entity}/entries/{id}';

  /**
   * Get the entity and entry information.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findEntityAndEntryById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findEntityAndEntryById$Response(params: {
    entity: string;
    id: string;
    includeNavigationLinks?: boolean;
    relationId?: string;
    parentId?: string;
    filter?: any;
    collectionId?: string;
    search?: string;
    parameters?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<DataEntryV2Body>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindEntityAndEntryByIdPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.query('includeNavigationLinks', params.includeNavigationLinks, {});
      rb.query('relationId', params.relationId, {});
      rb.query('parentId', params.parentId, {});
      rb.query('filter', params.filter, {});
      rb.query('collectionId', params.collectionId, {});
      rb.query('search', params.search, {});
      rb.query('parameters', params.parameters, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntryV2Body>;
      })
    );
  }
  /**
   * Get the entity and entry information.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findEntityAndEntryById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findEntityAndEntryById(params: {
    entity: string;
    id: string;
    includeNavigationLinks?: boolean;
    relationId?: string;
    parentId?: string;
    filter?: any;
    collectionId?: string;
    search?: string;
    parameters?: any;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<DataEntryV2Body> {

    return this.findEntityAndEntryById$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntryV2Body>) => r.body as DataEntryV2Body)
    );
  }

  /**
   * Path part for operation dataEntriesControllerUpdateByIdV2
   */
  static readonly DataEntriesControllerUpdateByIdV2Path = '/v2/entities/{entity}/entries/{id}';

  /**
   * Patch a single data entry that belongs to an entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateByIdV2()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateByIdV2$Response(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: Array<JsonPatchOperation>
  }): Observable<StrictHttpResponse<any>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerUpdateByIdV2Path, 'patch');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<any>;
      })
    );
  }
  /**
   * Patch a single data entry that belongs to an entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateByIdV2$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateByIdV2(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: Array<JsonPatchOperation>
  }): Observable<any> {

    return this.updateByIdV2$Response(params).pipe(
      map((r: StrictHttpResponse<any>) => r.body as any)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindV2
   */
  static readonly DataEntriesControllerFindV2Path = '/v2/entities/{entity}/entries';

  /**
   * Get a list of data entries that belong to an entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findV2()` instead.
   *
   * This method doesn't expect any request body.
   */
  findV2$Response(params: {
    entity: string;
    filterCriteria?: any;
    search?: string;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<DataEntryExcludingSearchIndex>>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindV2Path, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.query('filterCriteria', params.filterCriteria, {});
      rb.query('search', params.search, {});
      rb.query('include_virtual', params.include_virtual, {});
      rb.query('appliedQuickFilters', params.appliedQuickFilters, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<DataEntryExcludingSearchIndex>>;
      })
    );
  }
  /**
   * Get a list of data entries that belong to an entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findV2$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findV2(params: {
    entity: string;
    filterCriteria?: any;
    search?: string;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<DataEntryExcludingSearchIndex>> {

    return this.findV2$Response(params).pipe(
      map((r: StrictHttpResponse<Array<DataEntryExcludingSearchIndex>>) => r.body as Array<DataEntryExcludingSearchIndex>)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFetchReferenceFieldDataV2
   */
  static readonly DataEntriesControllerFetchReferenceFieldDataV2Path = '/v2/entities/{entity}/fields/{field}/data';

  /**
   * Get a list of users as reference fields for reference field with Included fields
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `fetchReferenceFieldDataV2()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  fetchReferenceFieldDataV2$Response(params: {
    entity: string;
    field: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: GetEntriesForFieldRequestDto
  }): Observable<StrictHttpResponse<Array<ReferenceProperty>>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFetchReferenceFieldDataV2Path, 'post');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('field', params.field, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ReferenceProperty>>;
      })
    );
  }
  /**
   * Get a list of users as reference fields for reference field with Included fields
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `fetchReferenceFieldDataV2$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  fetchReferenceFieldDataV2(params: {
    entity: string;
    field: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: GetEntriesForFieldRequestDto
  }): Observable<Array<ReferenceProperty>> {

    return this.fetchReferenceFieldDataV2$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ReferenceProperty>>) => r.body as Array<ReferenceProperty>)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindTreeV3
   */
  static readonly DataEntriesControllerFindTreeV3Path = '/v3/entities/{entity}/entries/tree';

  /**
   * Get a list of data entries that child to another entry with applied tags filters
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findTreeV3()` instead.
   *
   * This method doesn't expect any request body.
   */
  findTreeV3$Response(params: {
    entity: string;
    startingItem?: Array<string>;
    field?: string;
    appliedTagsFilters?: Array<string>;
    appliedQuickFilters?: Array<string>;
    expandAllChildren?: boolean;
    filterCriteria?: any;
    search?: string;
    relationId?: string;
    relationEntry?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<DataEntryTreeDtoV2>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindTreeV3Path, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.query('startingItem', params.startingItem, {});
      rb.query('field', params.field, {});
      rb.query('appliedTagsFilters', params.appliedTagsFilters, {});
      rb.query('appliedQuickFilters', params.appliedQuickFilters, {});
      rb.query('expandAllChildren', params.expandAllChildren, {});
      rb.query('filterCriteria', params.filterCriteria, {});
      rb.query('search', params.search, {});
      rb.query('relationId', params.relationId, {});
      rb.query('relationEntry', params.relationEntry, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntryTreeDtoV2>;
      })
    );
  }
  /**
   * Get a list of data entries that child to another entry with applied tags filters
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findTreeV3$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findTreeV3(params: {
    entity: string;
    startingItem?: Array<string>;
    field?: string;
    appliedTagsFilters?: Array<string>;
    appliedQuickFilters?: Array<string>;
    expandAllChildren?: boolean;
    filterCriteria?: any;
    search?: string;
    relationId?: string;
    relationEntry?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<DataEntryTreeDtoV2> {

    return this.findTreeV3$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntryTreeDtoV2>) => r.body as DataEntryTreeDtoV2)
    );
  }

  /**
   * Path part for operation dataEntriesControllerPatchDataEntryV3
   */
  static readonly DataEntriesControllerPatchDataEntryV3Path = '/v3/entities/{entity}/entries/{id}';

  /**
   * Patch data entry and related entries
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `patchDataEntryV3()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  patchDataEntryV3$Response(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: Array<PatchDataEntryDtoV3>
  }): Observable<StrictHttpResponse<any>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerPatchDataEntryV3Path, 'patch');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<any>;
      })
    );
  }
  /**
   * Patch data entry and related entries
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `patchDataEntryV3$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  patchDataEntryV3(params: {
    entity: string;
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: Array<PatchDataEntryDtoV3>
  }): Observable<any> {

    return this.patchDataEntryV3$Response(params).pipe(
      map((r: StrictHttpResponse<any>) => r.body as any)
    );
  }

  /**
   * Path part for operation dataEntriesControllerFindV3
   */
  static readonly DataEntriesControllerFindV3Path = '/v3/entities/{entity}/entries';

  /**
   * Get a list of data entries that belong to an entity and applied query tags
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findV3()` instead.
   *
   * This method doesn't expect any request body.
   */
  findV3$Response(params: {
    entity: string;
    filterCriteria?: any;
    search?: string;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    appliedTagsFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<DataEntriesResponseV3>> {

    const rb = new RequestBuilder(this.rootUrl, DataEntriesControllerService.DataEntriesControllerFindV3Path, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.query('filterCriteria', params.filterCriteria, {});
      rb.query('search', params.search, {});
      rb.query('include_virtual', params.include_virtual, {});
      rb.query('appliedQuickFilters', params.appliedQuickFilters, {});
      rb.query('appliedTagsFilters', params.appliedTagsFilters, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<DataEntriesResponseV3>;
      })
    );
  }
  /**
   * Get a list of data entries that belong to an entity and applied query tags
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findV3$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findV3(params: {
    entity: string;
    filterCriteria?: any;
    search?: string;
    include_virtual?: boolean;
    appliedQuickFilters?: Array<string>;
    appliedTagsFilters?: Array<string>;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<DataEntriesResponseV3> {

    return this.findV3$Response(params).pipe(
      map((r: StrictHttpResponse<DataEntriesResponseV3>) => r.body as DataEntriesResponseV3)
    );
  }

}
